<template>
  <portlet
    :icon="'fad fa-users'"
    :title="'Hours approval'"
    :actionsBlock="true"
  >
    <template slot="portletBody">
      <hours-header
        v-if="currentDate && Can(rights.HOURS_VIEW)"
        :export-blocked-reason="exportBlockedReason"
        :year="isoYear"
        :week="isoWeek"
        :approved-hours="approvedHoursLabel"
        @moveWeek="onMoveWeek"
        @importHours="importHours"
        @saveHours="saveHours"
        @exportHours="exportHours"
      />
      <datatables
        v-if="renderedComponent && Can(rights.HOURS_VIEW)"
        :key="tableKey"
        :tableClass="tableClass"
        tableID="hours"
        :rows="tableData"
        :columns="tableColumns"
        :tableInitOptions="mergedTableInitOptions"
        routerLinkClass="dt-router-link"
        deleteClass="delete"
      ></datatables>
      <div v-else>Not Allowed</div>
      <MessageModal
        v-if="showMessageModal && selectedEmployee"
        :employee-name="selectedEmployee.employeeName"
        :employment-id="selectedEmployee.employmentId"
        @close="showMessageModal = false"
        @sendMessage="handleSendMessage"
      />
    </template>
  </portlet>
</template>

<script>
  import {mapGetters} from 'vuex';
  import {dataTables} from '../../components/mixins/dataTables.js';
  import {swalModals} from '../../components/mixins/swalModals.js';
  import HoursDetail from '../../components/hours/HoursDetail.vue';
  import DateTools from '../../plugins/dateTools.js';
  import HoursHeader from '../../components/hours/HoursHeader.vue';
  import MessageModal from '../../components/hours/MessageModal.vue';
  import HoursTabs from '../../components/hours/HoursTabs.vue';
  import { HOURS_TABS, sumOf, tabRowsOf, tabDefinition } from './hoursTabs.js';
  import Vue from 'vue';
  import store from '../../store.js';
  import editUser from '../../components/users/editUser.vue';
  import { Can } from '../../../permission/ability.js';
  import { RIGHTS } from '../roles/rights';
  import moment from 'moment';
  import escapeHtml from 'lodash/escape';
  import sumBy from 'lodash/sumBy';
  import mapValues from 'lodash/mapValues';
  import { buildWeekRows, shiftsFor } from './weekRows.js';
  import { hasEverySearchWord, searchTextOf, searchWordsOf } from './tableSearch.js';
  import { describeApiOutcome } from '../../utils/apiOutcome.js';

  const NO_APP_ACCESS_TITLE = 'No app access anymore';
  const ROSTER_LOAD_FAILED = "Could not load this week's employees. Please refresh the page.";
  const HOURS_LOAD_FAILED = "Could not load this week's hours. Please refresh the page.";
  const WEEK_NOT_LOADED = 'Kon de uren van deze week niet laden.';
  const WEEK_IN_VIEW_NOT_LOADED_YET = 'De uren van deze week zijn nog niet geladen.';
  const SAVE_BEFORE_EXPORTING_OPEN_HOURS = 'Sla eerst je wijzigingen op, dan gaan ze mee in de export.';
  const EXPORT_FAILED = 'Export mislukt, er is geen bestand opgehaald.';
  const EXPORT_IN_PROGRESS = 'De export loopt nog.';
  const TABLE_TOOLBAR_WITH_TABS_SLOT = '<"hours-table-toolbar"l<"hours-tabs-slot">f>rtip';
  const TABS_SLOT_SELECTOR = '.hours-tabs-slot';
  const ACTIVE_TAB_SELECTOR = '.hours-tab.active';
  const DATATABLES_DEFAULT_PAGE_LENGTH = 10;
  const NO_LOAD_SHOWN_YET = 0;
  const TOTAL_HOURS_COLUMN = 'Total Hours';
  const OVERTIME_COLUMN = 'Overtime';
  const EXTRA_PAYMENT_COLUMN = 'Extra Payment (€)';
  const ROW_TOTAL_COLUMNS = [TOTAL_HOURS_COLUMN, OVERTIME_COLUMN, EXTRA_PAYMENT_COLUMN].map(column => `${column}:name`);

  export default {
    components: { 
      editUser,
      HoursHeader,
      MessageModal
    },
    mixins: [
      dataTables,
      swalModals
    ],
    data() {
      return {
        rights: '',
        isAdmin: false,
        currentDate: this.$route.params.date ? new Date(this.$route.params.date) : new Date(),
        shifts: {},
        weekRowsByEmployment: {},
        fullyLoadedWeek: null,
        tableSearchTerm: '',
        tablePageLength: DATATABLES_DEFAULT_PAGE_LENGTH,
        fetchSequence: 0,
        updatedRows: Object.create(null),
        newRows: Object.create(null),
        isHorizontal: true,
        showMessageModal: false,
        selectedEmployee: null,
        activeTab: HOURS_TABS.OPEN,
        refocusActiveTab: false,
        rowsByTab: tabRowsOf([]),
        shownRunId: NO_LOAD_SHOWN_YET,
        tableMounts: [],
        isExporting: false
      };
    },
    computed: {
      isoWeek() { return DateTools.isoWeekYear(this.currentDate).week; },
      isoYear() { return DateTools.isoWeekYear(this.currentDate).year; },
      selectedDepartmentName() {
        const { selectedDepartment, departments } = this.$store.getters.getUserData;

        if (selectedDepartment) return selectedDepartment;

        return departments && departments.length ? departments[0].name : null;
      },
      tableData() {
        return this.rowsByTab[this.activeTab];
      },
      tableColumns() {
        return this.getColumns();
      },
      hasFailedToLoadTheShownWeek() {
        return this.shownRunId !== NO_LOAD_SHOWN_YET && this.fullyLoadedWeek === null;
      },
      isLoadedWeekInView() {
        const loaded = this.fullyLoadedWeek;

        return Boolean(loaded) && loaded.week === this.isoWeek && loaded.year === this.isoYear;
      },
      searchTextByEmployment() {
        return mapValues(this.weekRowsByEmployment, row => searchTextOf(row, this.tableColumns));
      },
      approvedHoursLabel() {
        if (Can(RIGHTS.IS_CUSTOMER) || !this.isLoadedWeekInView) return null;

        const searchWords = searchWordsOf(this.tableSearchTerm);
        const rowsInSearch = this.tableData
          .filter(row => hasEverySearchWord(this.searchTextByEmployment[row.employmentId], searchWords));

        return this.formatNum(sumBy(rowsInSearch, 'approvedHours'));
      },
      ...mapGetters('roles', ['getRoles']),
      tableKey() {
        return `${this.activeTab}-${this.shownRunId}`;
      },
      exportBlockedReason() {
        if (this.isExporting) return EXPORT_IN_PROGRESS;
        if (this.hasFailedToLoadTheShownWeek) return WEEK_NOT_LOADED;
        if (!this.isLoadedWeekInView) return WEEK_IN_VIEW_NOT_LOADED_YET;

        return tabDefinition(this.activeTab).exportBlockedReasonFor(this.tableData);
      },
      mergedTableInitOptions() {
        const vue = this;

        const _week = this.isoWeek;
        const _year = this.isoYear;
        const { emptyMessage, locksDays } = tabDefinition(this.activeTab);
        const emptyTabMessage = this.hasFailedToLoadTheShownWeek ? WEEK_NOT_LOADED : emptyMessage;

        function showChild(row, data, $tr) {
          if (!row.child()) {
            const placeholder = document.createElement('div');
            row.child(placeholder);

            vue.mountInTable(placeholder, {
              render(h) {
                return h(HoursDetail, {
                  props: {
                    data,
                    shifts: shiftsFor(vue.shifts, data.organizationId),
                    isHorizontal: vue.isHorizontal,
                    readOnly: locksDays
                  },
                  on: {
                    'detail-updated': (details, changedRow) => {
                      row.table().cells(row.index(), ROW_TOTAL_COLUMNS).invalidate();

                      // Updated Rows
                      if(changedRow.id !== null){
                        if(!vue.updatedRows[changedRow.employment.id]){
                          vue.updatedRows[changedRow.employment.id] = Object.create(null);
                        }
                        vue.updatedRows[changedRow.employment.id][changedRow.date] = changedRow;
                      
                      // New Rows
                      } else {
                        if(!vue.newRows[changedRow.employment.id]){
                          vue.newRows[changedRow.employment.id] = Object.create(null);
                        }
                        vue.newRows[changedRow.employment.id][changedRow.date] = changedRow;
                      }
                    }
                  }
                });
              }
            });
          }

          row.child.show();
          $tr.addClass('shown');
        }

        return {
          ...this.tableInitOptions,
          responsive: false,
          dom: TABLE_TOOLBAR_WITH_TABS_SLOT,
          search: { search: this.tableSearchTerm },
          pageLength: this.tablePageLength,
          language: {
            ...this.tableInitOptions.language,
            sEmptyTable: emptyTabMessage
          },
          stateSaveParams(settings, state) {
            state.hoursTab = vue.activeTab;
          },
          stateLoadParams(settings, state) {
            if (state.hoursTab !== vue.activeTab) state.start = 0;
          },
          initComplete() {
            vue.destroyTableMounts();
            const api = this.api();
            vue.followTableState(api);
            api.on('search length', () => vue.followTableState(api));
            api.on('click', '#rotate-btn', () => vue.rotateTable());
            api.on('click', '.employee-action-btn', function () {
              const employmentId = $(this).data('employment-id');
              const employeeName = $(this).data('employee-name');
              vue.selectedEmployee = { employmentId, employeeName };
              vue.showMessageModal = true;
            });
            api.on('click', 'td.dt-control', function () {
              const $tr  = $(this).closest('tr');
              const row  = api.row($tr);

              if (row.child.isShown()) {
                row.child.hide();
                $tr.removeClass('shown');
              } else {
                showChild(row, row.data(), $tr);
              }
            });
            vue.mountTabs(api.table().container().querySelector(TABS_SLOT_SELECTOR));
          }
        };
      }
    },
    watch: {
      currentDate: {
        handler: function (date) {
          this.fetchData();
        }
      },
      '$route.params.date': {
        handler: function (newDate) {
          if (newDate) {
            this.currentDate = new Date(newDate);
          }
        }
      }
    },
    async created() {
      this.rights = RIGHTS;
      await  this.fetchData();
      this.isAdmin = this.$store.state.users.me.admin;
    },
    beforeDestroy() {
      this.destroyTableMounts();
    },
    methods: {
      Can,
      formatNum(val) {
        return Number(val).toFixed(2)
          .replace(/\.0+$/, '')
          .replace(/(\.[1-9]*)0+$/, '$1')
          .replace(/^0+(\.0+)?$/, '0');
      },
      /**
       * Rebuild the week's table. Overlapping runs are normal here — the two
       * watchers and saveHours() can each start one — so every run takes a
       * sequence number and only the newest commits its results.
       */
      async fetchData() {
        const runId = ++this.fetchSequence;
        const isCurrentRun = () => this.fetchSequence === runId;

        this.$store.commit('setLoading', true);

        try {
          const weekDaysAtRequestTime = DateTools.getWeekDays(this.currentDate);
          const weekAtRequestTime = DateTools.isoWeekYear(this.currentDate);

          const [[roster, shifts], hoursRecords] = await Promise.all([
            this.fetchRosterAndItsShifts(),
            this.fetchWeekHours()
          ]);
          if (!isCurrentRun()) return;

          const hasLoadedTheWholeWeek = roster !== null && hoursRecords !== null;
          const weekRows = buildWeekRows(roster || [], hoursRecords || [], shifts, weekDaysAtRequestTime);

          this.shownRunId = runId;
          this.shifts = shifts;
          this.rowsByTab = tabRowsOf(Object.values(weekRows));
          this.weekRowsByEmployment = weekRows;
          this.fullyLoadedWeek = hasLoadedTheWholeWeek ? weekAtRequestTime : null;
        } finally {
          // Only the newest run owns the overlay. A superseded run leaves it up
          // for whichever run replaced it, and a throw cannot strand it.
          if (isCurrentRun()) this.$store.commit('setLoading', false);
        }
      },
      async fetchRosterAndItsShifts() {
        const roster = await this.fetchRoster();

        return [roster, await this.fetchShifts(roster || [])];
      },
      async fetchRoster() {
        const { week, year } = DateTools.isoWeekYear(this.currentDate);

        const rosterRes = await store.dispatch('hours/getRoster', {
          ApiClient:   Vue.prototype.$ApiClient,
          department:  this.selectedDepartmentName,
          week,
          year
        });

        if (rosterRes.status !== 200) {
          this.$toasted.global.failedRequest({ message: ROSTER_LOAD_FAILED });
          return null;
        }

        return rosterRes.data.data;
      },
      async fetchWeekHours() {
        const hoursRecords = await this.fetchHours();

        if (hoursRecords === null) this.$toasted.global.failedRequest({ message: HOURS_LOAD_FAILED });

        return hoursRecords;
      },
      async fetchHours() {
        const weekInfo   = DateTools.isoWeekYear(this.currentDate);
        const { week, year } = weekInfo;

        const hoursRes = await store.dispatch('hours/getAllHours', {
          ApiClient:   Vue.prototype.$ApiClient,
          department:  this.selectedDepartmentName,
          week,
          year
        });

        if (hoursRes.status !== 200) return null;

        return hoursRes.data.data;
      },
      async fetchShifts(roster) {
        const organizationIds = new Set(roster
          .filter(placement => placement.organization)
          .map(placement => placement.organization.id));

        const shifts = {};
        await Promise.all([...organizationIds].map(async orgId => {
          const shiftsRes = await store.dispatch('shifts/getAll', {
            ApiClient:   Vue.prototype.$ApiClient,
            departments: this.$store.getters.getUserData.departments,
            company_id: orgId
          });

          shifts[orgId] = shiftsRes.status === 200 ? shiftsRes.data.data : [];
        }));

        return Object.freeze(shifts);
      },
      followTableState(api) {
        this.tableSearchTerm = api.search();
        this.tablePageLength = api.page.len();
      },
      onMoveWeek(direction) {
        this.updatedRows = Object.create(null);
        this.newRows = Object.create(null);
        this.currentDate = new Date(this.currentDate.getTime() + direction * 7*24*3600*1000);
      },
      selectTab(tab) {
        if (tab === this.activeTab) return;

        this.activeTab = tab;
        this.refocusActiveTab = true;
      },
      mountInTable(target, options) {
        const mounted = new Vue({ parent: this, ...options }).$mount(target);
        this.tableMounts.push(mounted);

        return mounted;
      },
      destroyTableMounts() {
        this.tableMounts.forEach(mount => mount.$destroy());
        this.tableMounts = [];
      },
      mountTabs(slot) {
        const vue = this;

        const tabs = this.mountInTable(slot, {
          render(createElement) {
            return createElement(HoursTabs, {
              props: { activeTab: vue.activeTab },
              on: { select: vue.selectTab }
            });
          }
        });

        if (!this.refocusActiveTab) return;

        this.refocusActiveTab = false;
        tabs.$el.querySelector(ACTIVE_TAB_SELECTOR).focus();
      },
      async saveHours () {
        if (!this.hasUnsavedChanges()) {
          this.$toasted.global.info({ message: 'Nothing to save.' });
          return;
        }

        this.$store.commit('setLoading', true);
        let hadError = false;

        try {
          const toCreate = [];
          const toUpdate = [];
        
          // Dynamic build toUpdate array based on existing id or not
          for(const row of Object.values(this.updatedRows)){
            toUpdate.push(row);
          }
          

          for (const [employmentId, byDate] of Object.entries(this.newRows)) {
            for (const rec of Object.values(byDate)) {
              if(rec.id){
                  toUpdate.push({
                      id: rec.id,
                      status: rec.status,
                      hours: rec.hours,
                      overtime: rec.overtime,
                      extra_payment: rec.extra_payment,
                      shift_allowance: rec.shift_allowance,
                      shift_id      : rec.shift && rec.shift.id,
                      byDate: rec.byDate,
                  });
              } else {
                toCreate.push({
                    employment_id : Number(employmentId),
                    date          : rec.date,
                    hours         : rec.hours,
                    overtime      : rec.overtime,
                    shift_id      : rec.shift && rec.shift.id,
                    extra_payment : rec.extra_payment,
                    shift_allowance: rec.shift_allowance,
                    status        : rec.status,
                });
              }
            }
          }

          if (toCreate.length) {
            const res = await store.dispatch('hours/createHours', {
              ApiClient  : Vue.prototype.$ApiClient,
              departments: this.$store.getters.getUserData.departments,
              body       : { hours: toCreate },
            });

            if (res.status !== 200) hadError = true;
          }

          // Update hours
          for (const byDate of Object.values(toUpdate)) {
            for (const rec of Object.values(byDate)) {
                const res = await store.dispatch('hours/updateHour', {
                ApiClient   : Vue.prototype.$ApiClient,
                departments : this.$store.getters.getUserData.departments,
                hourId      : rec.id,
                status      : rec.status,
                hours       : rec.hours,
                overtime    : rec.overtime,
                extra_payment: rec.extra_payment,
                shift_allowance: rec.shift_allowance,
                shift_id      : rec.shift && rec.shift.id,
              });
              // Update shift if shift_id is provided
              if (res.status !== 200) {
                hadError = true
              } else {
                // Disable status select if the hour is approved
                $('td[data-type="status"] select').val('approved').attr('disabled', true);
              }
            }
          }

          if (hadError) {
            this.$toasted.global.failedRequest({
              message: 'Some hours could not be saved – please try again.',
            });
          } else {
            this.$toasted.global.successRequest({
              message: 'Hours saved successfully!',
            });
            this.newRows    = Object.create(null);
            this.updatedRows = Object.create(null);
          }
        } catch (err) {
          console.error(err);
          this.$toasted.global.failedRequest({
            message: 'Unexpected error while saving hours.',
          });
        } finally {
          await this.fetchData();
        }
      },
      async exportHours(format) {
        if (this.hasUnsavedChanges()) {
          this.$toasted.global.info({ message: SAVE_BEFORE_EXPORTING_OPEN_HOURS });
          return;
        }

        this.isExporting = true;

        try {
          await this.exportAndReload(format);
        } finally {
          this.isExporting = false;
        }
      },
      async exportAndReload(format) {
        try {
          this.downloadExport(await this.exportOpenHours(format), format);
        } finally {
          await this.fetchData();
        }
      },
      hasUnsavedChanges() {
        return Object.keys(this.updatedRows).length > 0 || Object.keys(this.newRows).length > 0;
      },
      async exportOpenHours(format) {
        const { week, year } = DateTools.isoWeekYear(this.currentDate);

        const exportRes = await store.dispatch('hours/exportOpenHours', {
          ApiClient:   Vue.prototype.$ApiClient,
          department:  this.selectedDepartmentName,
          week,
          year,
          format
        });

        return describeApiOutcome(exportRes);
      },
      downloadExport({ data: hoursExport, message }, format) {
        if (!this.isDownloadableExport(hoursExport)) {
          this.$toasted.global.failedRequest({
            message: message || EXPORT_FAILED,
          });
          return;
        }

        // TODO: support XLS
        let blob = null;
        
        if(format === 'csv'){
            blob = new Blob(
                [ '\uFEFF' + hoursExport],
                { type: 'text/csv;charset=utf-8' }
            );
        } else if(format === 'xls'){
            // The server returns a blob directly when responseType is set to 'blob'
            blob = new Blob(
                [hoursExport],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );
            format = 'xlsx';
        }
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'exportHours_' + parseInt(Math.random() * 1000000) + "." + format;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      isDownloadableExport(hoursExport) {
        return typeof hoursExport === 'string'
          ? hoursExport.length > 0
          : hoursExport instanceof Blob && hoursExport.size > 0;
      },
      async importHours(file) {
        const res = await store.dispatch('hours/createHoursCSV', {
          ApiClient: Vue.prototype.$ApiClient,
          departments: this.$store.getters.getUserData.departments,
          file
        });

        if (res.status !== 200) {
          this.$toasted.global.failedRequest({
            message: 'Unexpected error while saving hours.',
          });
        } else {
          this.$toasted.global.successRequest({
            message: 'Hours saved successfully!',
          });
        }
      },
      rotateTable() {
        this.isHorizontal = !this.isHorizontal;
      },
      async handleSendMessage(messageData) {
        this.$store.commit('setLoading', true);
        
        try {
          const now = new Date();
          const twoMinutesFromNow = new Date(now.getTime() + 2 * 60 * 1000);
          const timeString = moment(twoMinutesFromNow).utc().format('HH:mm');//.toTimeString().slice(0, 5); // HH:MM

          const reminderRes = await store.dispatch('notifications/createReminder', {
            ApiClient: Vue.prototype.$ApiClient,
            departments: this.$store.getters.getUserData.departments,
            message: messageData.message,
            day: null,
            subject: messageData.subject,
            time: timeString,
            type: 'one-off',
            userIds: [this.weekRowsByEmployment[messageData.employmentId].id],
            allowAnswer: messageData.allowAnswer
          });
          
          if (reminderRes.status === 200) {
            this.$toasted.global.successRequest({
              message: `Message sent to ${messageData.employeeName}!`,
            });
          } else {
            this.$toasted.global.failedRequest({
              message: 'Failed to send message. Please try again.',
            });
          }
        } catch (error) {
          console.error('Error sending message:', error);
          this.$toasted.global.failedRequest({
            message: 'Unexpected error while sending message.',
          });
        } finally {
          this.$store.commit('setLoading', false);
        }
      },
      getColumns() {
        const online  = require('@/assets/icons/online.png');
        const offline = require('@/assets/icons/offline.png');
        const rotate = require('@/assets/icons/rotate_table.png');

        return [
          {data: 'change_password', name: '', visible: true, sortable: false, searchable: false, render(value) {
            const src = value ? offline : online;
            return `<img src="${src}" height="24" alt="">`;
          }},
          {data: null, name: 'Name', visible: true, searchable: true, render(data) {
            const name = escapeHtml(data.name);
            const endDate = data.shownEndDate
              ? ' <span class="text-muted small">ended ' + moment(data.shownEndDate).format('DD-MM-YYYY') + '</span>'
              : '';

            return '<a data-to="/documents/'+ data.employmentId + '/1/' + name + '" class="action-icon dt-router-link">' + name + '</a>' + endDate
          }},
          {data: 'organization', name: 'Organization', visible: true, searchable: true},
          {data: row => sumOf(row._hoursDetails, 'hours'), name: TOTAL_HOURS_COLUMN, visible: true, searchable: false, render: (value, type, row) => this.formatNum(value)},
          {data: row => sumOf(row._hoursDetails, 'overtime'), name: OVERTIME_COLUMN, visible: true, searchable: false, render: (value, type, row) => this.formatNum(value)},
          {data: row => sumOf(row._hoursDetails, 'extra_payment'), name: EXTRA_PAYMENT_COLUMN, visible: this.Can('HOURS_EXTRA_PAYMENT'), searchable: false, render: (value, type, row) => this.formatNum(value)},
          {data: 'approvalStatus', name: 'Approved', visible: true, sortable: false, searchable: false, render: (value, type, row) => {
            if (value === 'none') return '';
            if (value === 'all') return '<i class="fas fa-check text-success"></i>';
            if (value === 'partial') return '<i class="fas fa-equals text-warning"></i>';
            return '<i class="fas fa-times text-danger"></i>';
          }},
          {data: 'approved_by', name: 'Approved By', visible: true, sortable: false, searchable: false},
          {
            data: null,
            name: 'Message',
            visible: true,
            sortable: false,
            searchable: false,
            render: (value, type, row) => {
              if (!row.canReceiveMessages) {
                return `<button class="btn btn-primary btn-sm" disabled title="${NO_APP_ACCESS_TITLE}"><i class="fas fa-envelope"></i></button>`;
              }

              return `<button class="btn btn-primary btn-sm employee-action-btn" data-employment-id="${row.employmentId}" data-employee-name="${escapeHtml(row.name)}"><i class="fas fa-envelope"></i></button>`;
            }
          },
          {
            data: null,
            title: `<button id="rotate-btn" class="btn"><img src="${rotate}" height="24" alt=""></button>`,
            className: 'dt-control',
            orderable: false,
            defaultContent: ''
          },
        ]
      }
    }
  };
</script>

<style scoped>
  ::v-deep td.dt-control::before {
    font-size: 1.5rem;
    font-family: "Font Awesome 5 Pro";
    content: "\f106";
    cursor: pointer;
    margin-right: .5em;
  }
  ::v-deep tr.shown td.dt-control::before {
    content: "\f107";
  }
  ::v-deep #rotate-btn {
    background: transparent;
    border: none;
  }
  ::v-deep .hours-table-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 1rem;
  }
  ::v-deep .hours-table-toolbar .dataTables_filter {
    margin-left: auto;
  }
</style>
